import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import {
  Search,
  Add,
  Visibility,
  Edit,
  Close,
  Favorite,
  Work,
  Psychology,
  Business,
} from "@mui/icons-material";
import CustomInputField from "../common/CustomInputField";
import { reportService } from "../../services/reportService";
import { CHAT_CATEGORY_OPTIONS } from "../../constants/apiConstants";
import { systemPromptService } from "../../services/systemPromptService";

const Report = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter] = useState("Type Filter");
  const [categoryFilter, setCategoryFilter] = useState("All Category");
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [neutralMessage, setNeutralMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [subTitleLimitExceeded, setSubTitleLimitExceeded] = useState(false);
  const [reports, setReports] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [systemCategories, setSystemCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [editingReport, setEditingReport] = useState(null);
  const [, setViewingReport] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [originalReportForm, setOriginalReportForm] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch reports on component mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // load reports initially (no category filter)
    loadReports();
    // load categories for select/autocomplete
    loadSystemCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load reports helper (accepts optional category param)
  const loadReports = async (category) => {
    setFetchLoading(true);
    try {
      const params = category && category !== "All Category" ? { category } : {};
      const result = await reportService.getReports(params);
      if (result.success) {
        const transformedReports = result.data.map((report) => ({
          id: report._id?.$oid || report.id,
          title: report.name,
          description: report.sub_title || report.description,
          type: report.type,
          prompt: report.prompt,
          icon: getReportIcon(report.type),
          tags: [report.type],
          color: getReportColor(report.type),
        }));
        setReports(transformedReports);
        setTotalPages(Math.ceil(transformedReports.length / itemsPerPage));
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("Failed to fetch reports. Please try again.");
    } finally {
      setFetchLoading(false);
    }
  };

  const loadSystemCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await systemPromptService.getSystemPrompts();
      if (res.success) {
        // API may return an array directly or an object with `result` or `data` array
        const payload = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.result)
            ? res.data.result
            : Array.isArray(res.data?.data)
              ? res.data.data
              : [];

        if (payload.length > 0) {
          const categories = Array.from(
            new Set(
              payload
                .map((item) => item.category)
                .filter((c) => c !== undefined && c !== null)
            )
          ).map((value) => ({
            value,
            label: String(value)
              .replace(/_/g, " ")
              .replace(/\w\S*/g, (w) => w.replace(/^(\w)/, (c) => c.toUpperCase())),
          }));

          setSystemCategories(categories);
        } else {
          setSystemCategories(CHAT_CATEGORY_OPTIONS);
        }
      } else {
        setSystemCategories(CHAT_CATEGORY_OPTIONS);
      }
    } catch (err) {
      setSystemCategories(CHAT_CATEGORY_OPTIONS);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Helper function to get icon based on type
  const getReportIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "love":
        return <Favorite sx={{ color: "#E91E63" }} />;
      case "career":
        return <Work sx={{ color: "#2196F3" }} />;
      case "business":
      case "wealth":
        return <Business sx={{ color: "#FF9800" }} />;
      case "health":
        return <Psychology sx={{ color: "#4CAF50" }} />;
      case "family":
        return <Favorite sx={{ color: "#9C27B0" }} />;
      case "education":
        return <Psychology sx={{ color: "#FF5722" }} />;
      case "travel_and_relocation":
        return <Business sx={{ color: "#607D8B" }} />;
      case "mental_wellbeing":
        return <Psychology sx={{ color: "#795548" }} />;
      case "personality":
        return <Psychology sx={{ color: "#673AB7" }} />;
      default:
        return <Psychology sx={{ color: "#9C27B0" }} />;
    }
  };

  // Helper function to get color based on type
  const getReportColor = (type) => {
    switch (type?.toLowerCase()) {
      case "love":
        return "#E91E63";
      case "career":
        return "#2196F3";
      case "business":
      case "wealth":
        return "#FF9800";
      case "health":
        return "#4CAF50";
      case "family":
        return "#9C27B0";
      case "education":
        return "#FF5722";
      case "travel_and_relocation":
        return "#607D8B";
      case "mental_wellbeing":
        return "#795548";
      case "personality":
        return "#673AB7";
      default:
        return "#9C27B0";
    }
  };

  const [reportForm, setReportForm] = useState({
    reportName: "",
    type: "",
    subTitle: "",
    description: "",
    prompt: "",
  });

  // countWords removed — not required currently

  const handleReportFormChange = (field, value) => {
    setReportForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenModal = () => {
    setModalMode("add");
    setViewingReport(null);
    setEditingReport(null);
    setError("");
    setSuccess("");
    setNeutralMessage("");
    setOriginalReportForm({
      reportName: "",
      type: "",
      subTitle: "",
      description: "",
      prompt: "",
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setError("");
    setSuccess("");
    setFieldErrors({});
    setEditingReport(null);
    setViewingReport(null);
    setModalMode("add");
    setReportForm({
      reportName: "",
      type: "",
      subTitle: "",
      description: "",
      prompt: "",
    });
    setNeutralMessage("");
    setOriginalReportForm(null);
  };

  const handleReset = () => {
    if (modalMode === "edit" && originalReportForm) {
      setReportForm({
        reportName: originalReportForm.reportName || "",
        type: originalReportForm.type || "",
        subTitle: originalReportForm.subTitle || "",
        description: originalReportForm.description || "",
        prompt: originalReportForm.prompt || "",
      });
    } else {
      setReportForm({
        reportName: "",
        type: "",
        subTitle: "",
        description: "",
        prompt: "",
      });
    }
  };

  const isFormChanged = () => {
    if (modalMode === "edit" && originalReportForm) {
      const current = {
        reportName: (reportForm.reportName || "").toString().trim(),
        type: (reportForm.type || "").toString().trim(),
        subTitle: (reportForm.subTitle || "").toString().trim(),
        description: (reportForm.description || "").toString().trim(),
        prompt: (reportForm.prompt || "").toString().trim(),
      };
      const original = {
        reportName: (originalReportForm.reportName || "").toString().trim(),
        type: (originalReportForm.type || "").toString().trim(),
        subTitle: (originalReportForm.subTitle || "").toString().trim(),
        description: (originalReportForm.description || "").toString().trim(),
        prompt: (originalReportForm.prompt || "").toString().trim(),
      };
      return JSON.stringify(current) !== JSON.stringify(original);
    }
    // add mode: any non-empty field means changed
    return Object.values(reportForm || {}).some((v) => (v || "").toString().trim() !== "");
  };

  const isReportUnchanged = () => {
    if (!originalReportForm) return false;
    try {
      const current = {
        reportName: reportForm.reportName || "",
        type: reportForm.type || "",
        subTitle: reportForm.subTitle || "",
        description: reportForm.description || "",
        prompt: reportForm.prompt || "",
      };
      const original = {
        reportName: originalReportForm.reportName || "",
        type: originalReportForm.type || "",
        subTitle: originalReportForm.subTitle || "",
        description: originalReportForm.description || "",
        prompt: originalReportForm.prompt || "",
      };
      return JSON.stringify(current) === JSON.stringify(original);
    } catch (err) {
      return false;
    }
  };

  const handleSubmitReport = async () => {
    setError("");
    setSuccess("");
    setFieldErrors({});

    // If editing and no changes were made, show neutral info and don't submit
    if (modalMode === "edit" && isReportUnchanged()) {
      setNeutralMessage("No changes to save.");
      setTimeout(() => setNeutralMessage(""), 3000);
      return;
    }

    // Validation
    const errors = {};

    if (!reportForm.reportName.trim()) {
      errors.reportName = "Report name is required";
    }

    if (!reportForm.type.trim()) {
      errors.type = "Report type is required";
    }

    if (!reportForm.subTitle.trim()) {
      errors.subTitle = "Sub title is required";
    }

    if (!reportForm.description.trim()) {
      errors.description = "Description is required";
    }

    if (!reportForm.prompt.trim()) {
      errors.prompt = "Prompt is required";
    } else if (reportForm.prompt.trim().length < 10) {
      errors.prompt = "Prompt must be at least 10 characters long";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);

    try {
      let result;
      if (editingReport) {
        // Update existing report
        result = await reportService.updateReport(editingReport, {
          name: reportForm.reportName.trim(),
          type: reportForm.type.trim(),
          sub_title: reportForm.subTitle.trim(),
          description: reportForm.description.trim(),
          prompt: reportForm.prompt.trim(),
        });
      } else {
        // Create new report
        result = await reportService.createReport({
          name: reportForm.reportName.trim(),
          type: reportForm.type.trim(),
          sub_title: reportForm.subTitle.trim(),
          description: reportForm.description.trim(),
          prompt: reportForm.prompt.trim(),
        });
      }

      if (result.success) {
        setSuccess(result.message);
        // Reset form
        setReportForm({
          reportName: "",
          type: "",
          subTitle: "",
          description: "",
          prompt: "",
        });
        // Refresh reports list with current category filter
        await loadReports(categoryFilter === "All Category" ? null : categoryFilter);
        // Reset to first page after adding/updating
        setCurrentPage(1);
        // Close modal after a short delay to show success message
        setTimeout(() => {
          handleCloseModal();
          setEditingReport(null);
        }, 2000);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (reportId) => {
    const report = reports.find((r) => r.id === reportId);
    if (report) {
      setReportForm({
        reportName: report.title,
        type: report.type,
        subTitle: report.description,
        description: report.description,
        prompt: report.prompt,
      });
      setModalMode("view");
      setViewingReport(reportId);
      setEditingReport(null);
      setModalOpen(true);
    }
  };

  const handleEditReport = (reportId) => {
    const report = reports.find((r) => r.id === reportId);
    if (report) {
      setReportForm({
        reportName: report.title,
        type: report.type,
        subTitle: report.description,
        description: report.description,
        prompt: report.prompt,
      });
      setModalMode("edit");
      setEditingReport(reportId);
      setViewingReport(null);
      setNeutralMessage("");
      setOriginalReportForm({
        reportName: report.title,
        type: report.type,
        subTitle: report.description,
        description: report.description,
        prompt: report.prompt,
      });
      setModalOpen(true);
    }
  };

  // Get filtered reports based on search and filters
  const getFilteredReports = () => {
    return reports.filter((report) => {
      const matchesSearch = report.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesType =
        typeFilter === "Type Filter" || report.type === typeFilter;
      const matchesCategory =
        categoryFilter === "All Category" || report.type === categoryFilter;
      return matchesSearch && matchesType && matchesCategory;
    });
  };

  // Get current page items for pagination
  const getCurrentPageItems = () => {
    const filtered = getFilteredReports();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  // Update total pages when filtered reports change
  const filteredReports = getFilteredReports();

  useEffect(() => {
    setTotalPages(Math.ceil(filteredReports.length / itemsPerPage));
    // Reset to first page when search changes
    setCurrentPage(1);
  }, [filteredReports.length, itemsPerPage]);

  return (
    <Box sx={{ backgroundColor: "#000000", minHeight: "100%", width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 4,
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 0 },
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: "#FFFFFF",
              fontWeight: 600,
              marginBottom: 1,
              fontSize: { xs: "24px", md: "32px" },
            }}
          >
            Customize Report
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#888888",
              fontSize: "16px",
            }}
          >
            Add / Edit Customize report as per user Kundali
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenModal}
          sx={{
            backgroundColor: "#E1AD01",
            color: "#000000",
            borderRadius: "25px",
            padding: "12px 24px",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#B8941F",
            },
          }}
        >
          Add Report
        </Button>
      </Box>

      {/* Search and Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          marginBottom: 4,
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
        }}
      >
        {/* Search Field */}
        <TextField
          placeholder="Search Reports"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#666666" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            maxWidth: { xs: "100%", md: "400px" },
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "25px",
              "& fieldset": {
                borderColor: "#E0E0E0",
              },
              "&:hover fieldset": {
                borderColor: "#E1AD01",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#E1AD01",
              },
            },
            "& .MuiInputBase-input": {
              padding: "12px 14px",
              color: "#333333",
              "&::placeholder": {
                color: "#666666",
                opacity: 1,
              },
            },
          }}
        />

        {/* Category Filter */}
        <Box sx={{ minWidth: 150 }}>
          <Select
            value={categoryFilter}
            onChange={(e) => {
              const val = e.target.value;
              setCategoryFilter(val);
              // Call API to fetch reports for selected category (or all if "All Category")
              loadReports(val === "All Category" ? null : val);
            }}
            displayEmpty
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "#1A1A1A",
                  border: "1px solid #333333",
                  borderRadius: "8px",
                  maxHeight: 300,
                  zIndex: 9999,
                  "& .MuiMenuItem-root": {
                    color: "#FFFFFF",
                    backgroundColor: "#1A1A1A",
                    fontSize: "14px",
                    padding: "12px 16px",
                    "&:hover": {
                      backgroundColor: "#333333",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#E1AD01",
                      color: "#000000",
                      "&:hover": {
                        backgroundColor: "#B8941F",
                      },
                    },
                  },
                },
              },
            }}
            sx={{
              backgroundColor: "#1A1A1A",
              color: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #333333",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiSelect-icon": {
                color: "#FFFFFF",
              },
            }}
          >
            <MenuItem value="All Category">All Categories</MenuItem>
            {categoriesLoading ? (
              <MenuItem disabled>Loading...</MenuItem>
            ) : systemCategories && systemCategories.length > 0 ? (
              systemCategories.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))
            ) : (
              CHAT_CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))
            )}
          </Select>
        </Box>
      </Box>

      {/* Reports List */}
      <Box sx={{ marginBottom: 4 }}>
        {fetchLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
            <CircularProgress sx={{ color: "#E1AD01" }} />
          </Box>
        ) : getCurrentPageItems().length === 0 ? (
          <Box sx={{ textAlign: "center", padding: 4 }}>
            <Typography sx={{ color: "#CCCCCC", fontSize: "18px" }}>
              No reports found
            </Typography>
          </Box>
        ) : (
          getCurrentPageItems().map((report) => (
            <Card
              key={report.id}
              sx={{
                backgroundColor: "#2A2A2A",
                borderRadius: "16px",
                marginBottom: 3,
                border: "1px solid #333333",
                overflow: "visible",
              }}
            >
              <CardContent sx={{ padding: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {report.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontSize: "20px",
                          fontWeight: 600,
                          marginBottom: 0.5,
                        }}
                      >
                        {report.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#CCCCCC",
                          fontSize: "14px",
                        }}
                      >
                        {report.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<Visibility />}
                      onClick={() => handleViewDetails(report.id)}
                      sx={{
                        backgroundColor: "#E1AD01",
                        color: "#000000",
                        borderRadius: "20px",
                        padding: "8px 16px",
                        width: "140px",
                        minWidth: "140px",
                        whiteSpace: "nowrap",
                        fontSize: "14px",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "#B8941F",
                        },
                      }}
                    >
                      View Details
                    </Button>
                    <IconButton
                      onClick={() => handleEditReport(report.id)}
                      sx={{
                        backgroundColor: "#E1AD01",
                        color: "#000000",
                        width: 40,
                        height: 40,
                        "&:hover": {
                          backgroundColor: "#B8941F",
                        },
                      }}
                    >
                      <Edit sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {report.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      sx={{
                        backgroundColor: "#4A5568",
                        color: "#FFFFFF",
                        fontSize: "12px",
                        height: 24,
                        "& .MuiChip-label": {
                          padding: "0 8px",
                        },
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* Pagination */}
      {filteredReports.length > itemsPerPage && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            sx={{
              borderColor: "#666666",
              color: "#CCCCCC",
              borderRadius: "20px",
              padding: "8px 20px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#E1AD01",
                backgroundColor: "rgba(212, 175, 55, 0.1)",
              },
              "&:disabled": {
                borderColor: "#333333",
                color: "#666666",
              },
            }}
          >
            Previous
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (page) => (
                <Box
                  key={page}
                  onClick={() => handlePageChange(page)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor:
                      page === currentPage ? "#E1AD01" : "#333333",
                    color: page === currentPage ? "#000000" : "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor:
                        page === currentPage ? "#B8941F" : "#444444",
                    },
                  }}
                >
                  {page}
                </Box>
              )
            )}
          </Box>

          <Button
            variant="contained"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            sx={{
              backgroundColor: "#E1AD01",
              color: "#000000",
              borderRadius: "20px",
              padding: "8px 20px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#B8941F",
              },
              "&:disabled": {
                backgroundColor: "#333333",
                color: "#666666",
              },
            }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Add Report Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#FFFFFF",
            borderRadius: "20px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "24px 32px 16px 32px",
          }}
        >
          <Typography
            sx={{
              color: "#333333",
              fontSize: "24px",
              fontWeight: 600,
            }}
          >
            {modalMode === "view"
              ? "View Report"
              : modalMode === "edit"
                ? "Edit Report"
                : "Add Report"}
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              color: "#666666",
              "&:hover": { backgroundColor: "#F0F0F0" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "0 32px 32px 32px" }}>
          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                marginBottom: 3,
                backgroundColor: "#ffebee",
                color: "#c62828",
                whiteSpace: "pre-line"
              }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert
              severity="success"
              sx={{
                marginBottom: 3,
                backgroundColor: "#e8f5e8",
                color: "#2e7d32",
              }}
            >
              {success}
            </Alert>
          )}

          {/* Neutral Alert for unchanged saves */}
          {neutralMessage && (
            <Alert
              severity="info"
              sx={{
                marginBottom: 3,
                backgroundColor: "#f1f8ff",
                color: "#155fa0",
              }}
            >
              {neutralMessage}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
              marginBottom: 3,
              marginTop: 2,
            }}
          >
            {/* Report Name */}
            <Box>
              <CustomInputField
                label="Report Name"
                placeholder="Type Report Name"
                value={reportForm.reportName}
                onChange={(e) => {
                  handleReportFormChange("reportName", e.target.value);
                  if (fieldErrors.reportName) {
                    setFieldErrors(prev => ({ ...prev, reportName: "" }));
                  }
                }}
                disabled={modalMode === "view"}
                autoFocus={modalMode !== "view"}
              />
              {fieldErrors.reportName && (
                <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                  {fieldErrors.reportName}
                </Typography>
              )}
            </Box>

            {/* Report Type */}
            <Box>
              <Box sx={{ position: "relative" }}>
                <Typography
                  sx={{
                    position: "absolute",
                    top: "-9px",
                    left: "14px",
                    backgroundColor: "rgba(204, 158, 73, 1)",
                    color: "black",
                    padding: "4px 12px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 500,
                    zIndex: 10,
                  }}
                >
                  Category
                </Typography>
                <Autocomplete
                  value={reportForm.type}
                  onChange={(event, newValue) => {
                    handleReportFormChange("type", newValue || "");
                    if (error) setError("");
                    if (fieldErrors.type) {
                      setFieldErrors(prev => ({ ...prev, type: "" }));
                    }
                  }}
                  disabled={modalMode === "view"}
                  options={
                    categoriesLoading
                      ? []
                      : systemCategories && systemCategories.length > 0
                        ? systemCategories.map((option) => option.value)
                        : CHAT_CATEGORY_OPTIONS.map((option) => option.value)
                  }
                  openOnFocus
                  disablePortal
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select category"
                      inputProps={{ ...params.inputProps, readOnly: true }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#FFFFFF",
                          borderRadius: "12px",
                          "& fieldset": {
                            borderColor: "#E1AD01",
                          },
                          "&:hover fieldset": {
                            borderColor: "#E1AD01",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#E1AD01",
                            borderWidth: "2px",
                          },
                          "&.Mui-disabled fieldset": {
                            borderColor: "#E1AD01",
                          },
                          "& input": {
                            padding: "14px 16px",
                            color: "#000000",
                          },
                          "& input.Mui-disabled": {
                            color: "#000000 !important",
                            WebkitTextFillColor: "#000000 !important",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    width: "100%",
                    "& .MuiAutocomplete-popupIndicator": {
                      color: "#E1AD01",
                    },
                    "& .MuiAutocomplete-inputRoot": {
                      paddingRight: "39px !important",
                    },
                  }}
                  ListboxProps={{
                    sx: {
                      backgroundColor: "#FFFFFF",
                      "& .MuiAutocomplete-option": {
                        color: "#333333",
                        fontSize: "14px",
                        padding: "12px 16px",
                        "&:hover": {
                          backgroundColor: "#F5F5F5",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#F5F5F5",
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: "#E1AD01",
                          color: "#000000",
                          "&:hover": {
                            backgroundColor: "#B8941F",
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
              {fieldErrors.type && (
                <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                  {fieldErrors.type}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Sub Title */}
          <Box sx={{ marginBottom: 3 }}>
            <CustomInputField
              label="Sub Title"
              placeholder="Type Here"
              value={reportForm.subTitle}
              onChange={(e) => {
                handleReportFormChange("subTitle", e.target.value);
                if (fieldErrors.subTitle) {
                  setFieldErrors(prev => ({ ...prev, subTitle: "" }));
                }
              }}
              multiline={true}
              rows={4}
              disabled={modalMode === "view"}
              maxWords={100}
              onLimitExceeded={setSubTitleLimitExceeded}
            />
            {fieldErrors.subTitle && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.subTitle}
              </Typography>
            )}
            {subTitleLimitExceeded && modalMode !== "view" && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                You cannot write more than 100 words
              </Typography>
            )}
            
          </Box>

          {/* Description */}
          <Box sx={{ marginBottom: 3 }}>
            <CustomInputField
              label="Description"
              placeholder="Type Here"
              value={reportForm.description}
              onChange={(e) => {
                handleReportFormChange("description", e.target.value);
                if (fieldErrors.description) {
                  setFieldErrors(prev => ({ ...prev, description: "" }));
                }
              }}
              multiline={true}
              rows={6}
              disabled={modalMode === "view"}
              
            />
            {fieldErrors.description && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.description}
              </Typography>
            )}
            
          </Box>

          {/* Prompt */}
          <Box sx={{ marginBottom: 4 }}>
            <CustomInputField
              label="Prompt"
              placeholder="Type Here"
              value={reportForm.prompt}
              onChange={(e) => {
                handleReportFormChange("prompt", e.target.value);
                if (fieldErrors.prompt) {
                  setFieldErrors(prev => ({ ...prev, prompt: "" }));
                }
              }}
              multiline={true}
              rows={6}
              disabled={modalMode === "view"}
              
            />
            {fieldErrors.prompt && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.prompt}
              </Typography>
            )}
            
          </Box>

          {modalMode !== "view" && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={!isFormChanged()}
                sx={{
                  borderColor: "#E1AD01",
                  color: "#E1AD01",
                  borderRadius: "25px",
                  padding: "12px 32px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&.Mui-disabled": {
                    borderColor: "#666",
                    color: "#666",
                    opacity: 0.5,
                    cursor: "not-allowed",
                    pointerEvents: "auto",
                  },
                  "&:hover": {
                    borderColor: "#B8941F",
                    backgroundColor: "rgba(212, 175, 55, 0.1)",
                  },
                  "&.Mui-disabled:hover": {
                    borderColor: "#666",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Reset
              </Button>
                <Button
                variant="contained"
                onClick={handleSubmitReport}
                disabled={loading || subTitleLimitExceeded || (modalMode === 'edit' && isReportUnchanged())}
                sx={{
                  backgroundColor: "#E1AD01",
                  color: "#000000",
                  borderRadius: "25px",
                  padding: "12px 32px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#B8941F",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#666666",
                    color: "#cccccc",
                    cursor: "not-allowed",
                    pointerEvents: "auto"
                  },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: "#000" }} />
                    {modalMode === "edit" ? "Updating..." : "Adding..."}
                  </Box>
                ) : modalMode === "edit" ? (
                  "Update Report"
                ) : (
                  "Add Report"
                )}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Report;
